// @flow
import { of, merge } from 'rxjs';
import { map, catchError, mergeMap, filter } from 'rxjs/operators';
import update from 'immutability-helper';
import { push } from 'react-router-redux';
import { ofType, StateObservable } from 'redux-observable';
import {
  getSessionApi, refreshSessionApi, setCustomSessionApi, logoutCustomSessionApi,
  loginApi, loginFbApi, logoutApi, registerApi, indexCustomSessionApi, setTrackingSessionApi,
} from './api';
import { changeLocale } from '../../modules/locale/duck';
import analytics from '../../analytics';

import type { Observable } from 'rxjs';
import type { ProfileData } from '../preferences/edit-profile/duck';
import type { User, Language } from '../../entities';
import type { HTTPError } from '../../error';
import type { RootState } from '../index';
import type { TrackingData } from '../../analytics';

type AuthAction = {
  +type: string;
  +user?: User;
  +token?: string;
  +firstName?: string;
  +lastName?: string;
  +email?: string;
  +password?: string;
  +country?: ?string;
  +locale?: string;
  +error?: HTTPError;
  +languageCode?: Language;
  +redirectTo?: string;
  +profileData?: ProfileData;
  +userId?: string;
  +uuid?: string;
  +anonymousId?: string;
  +trackingData?: TrackingData;
};

export type AuthState = {
  +isAuthing?: boolean;
  +authError?: HTTPError;
  +isRegistering?: boolean;
  +signUpError?: HTTPError;
  +registerSuccessState?: boolean;
  // Both are writtable
  authenticated?: boolean;
  user?: User;
};

// Actions
const AUTH_SET_CUSTOM_SESSION_SUCCESS = 'AUTH_SET_CUSTOM_SESSION_SUCCESS';
const AUTH_INDEX_CUSTOM_SESSION_SUCCESS = 'AUTH_INDEX_CUSTOM_SESSION_SUCCESS';

const AUTH_GET_SESSION_REQUEST = 'AUTH_GET_SESSION_REQUEST';
/*const AUTH_GET_SESSION_SUCCESS = 'AUTH_GET_SESSION_SUCCESS;*/
const AUTH_GET_SESSION_ERROR = 'AUTH_GET_SESSION_ERROR';

const AUTH_SET_TRACKING_SESSION = 'AUTH_SET_TRACKING_SESSION';
const AUTH_SET_TRACKING_SESSION_SUCCESS = 'AUTH_SET_TRACKING_SESSION_SUCCESS';
const AUTH_SET_TRACKING_SESSION_ERROR = 'AUTH_SET_TRACKING_SESSION_ERROR';

const AUTH_LOGIN_REQUEST = 'AUTH_LOGIN_REQUEST';
const AUTH_LOGIN_SUCCESS = 'AUTH_LOGIN_SUCCESS';
const AUTH_FB_LOGIN_REQUEST = 'AUTH_FB_LOGIN_REQUEST';
const AUTH_LOGIN_ERROR = 'AUTH_LOGIN_ERROR';

const AUTH_REGISTER_REQUEST = 'AUTH_REGISTER_REQUEST';
const AUTH_REGISTER_SUCCESS = 'AUTH_REGISTER_SUCCESS';
const AUTH_REGISTER_ERROR = 'AUTH_REGISTER_ERROR';

const AUTH_UPDATE_USER = 'AUTH_UPDATE_USER';

const AUTH_LOGOUT_REQUEST = 'AUTH_LOGOUT_REQUEST';
const AUTH_LOGOUT_SUCCESS = 'AUTH_LOGOUT_SUCCESS';

const AUTH_RESET_STATE = 'AUTH_RESET_STATE';

const AUTH_NOOP = 'AUTH_NOOP';


// Action Creators
export function setCustomSessionSuccess(uuid?: string): AuthAction {
  return {
    type: AUTH_SET_CUSTOM_SESSION_SUCCESS,
    uuid,
  }
}

export function indexCustomSessionSuccess(uuid?: string): AuthAction {
  return {
    type: AUTH_INDEX_CUSTOM_SESSION_SUCCESS,
    uuid,
  }
}

export function getSession(): AuthAction {
  return {
    type: AUTH_GET_SESSION_REQUEST,
  };
}

export function setTrackingSession(trackingData: TrackingData): AuthAction {
  return {
    type: AUTH_SET_TRACKING_SESSION,
    trackingData,
  };
}

export function setTrackingSessionSuccess(anonymousId: string): AuthAction {
  return {
    type: AUTH_SET_TRACKING_SESSION_SUCCESS,
    anonymousId,
  };
}

export function setTrackingSessionError(error: HTTPError): AuthAction {
  return {
    type: AUTH_SET_TRACKING_SESSION_ERROR,
    error,
  };
}

/*export function getSessionSuccess(user: User): AuthAction {
  return {
    type: AUTH_GET_SESSION_SUCCESS,
    user,
  };
}
*/
export function getSessionError(error: HTTPError): AuthAction {
  return {
    type: AUTH_GET_SESSION_ERROR,
    error,
  };
}

export function login(email: string, password: string, redirectTo?: string): AuthAction {
  return {
    type: AUTH_LOGIN_REQUEST,
    email,
    password,
    redirectTo,
  };
}

export function fbLogin(country: ?string, language: Language, token: string,
                        email?: string, redirectTo?: string): AuthAction {
  return {
    type: AUTH_FB_LOGIN_REQUEST,
    country,
    language,
    token,
    email,
    redirectTo,
  };
}

export function loginSuccess(user: User, redirectTo?: string): AuthAction {
  return {
    type: AUTH_LOGIN_SUCCESS,
    user,
    redirectTo,
  };
}

export function loginError(error: HTTPError): AuthAction {
  return {
    type: AUTH_LOGIN_ERROR,
    error,
  }
}


export function register(country: string, firstName: string, lastName: string, email: string,
                         password: string, language: Language, redirectTo?: string): AuthAction {
  return {
    type: AUTH_REGISTER_REQUEST,
    country,
    firstName,
    lastName,
    email,
    password,
    language,
    redirectTo,
  }
}

export function updateUser(profileData: ProfileData): AuthAction {
  return {
    type: AUTH_UPDATE_USER,
    profileData,
  }
}

export function registerSuccess(user: User, redirectTo?: string): AuthAction {
  return {
    type: AUTH_REGISTER_SUCCESS,
    user,
    redirectTo,
  }
}

export function registerError(error: HTTPError): AuthAction {
  return {
    type: AUTH_REGISTER_ERROR,
    error,
  }
}

export function logout(userId: string): AuthAction {
  return {
    type: AUTH_LOGOUT_REQUEST,
    userId,
  }
}

export function logoutSuccess(): AuthAction {
  return {
    type: AUTH_LOGOUT_SUCCESS,
  }
}

export function resetAuthState(): AuthAction {
  return {
    type: AUTH_RESET_STATE,
  };
}

export function noop(): AuthAction {
  return {
    type: AUTH_NOOP,
  };
}

export const actions = {
  AUTH_SET_CUSTOM_SESSION_SUCCESS,
  AUTH_INDEX_CUSTOM_SESSION_SUCCESS,
  AUTH_GET_SESSION_REQUEST,
  // AUTH_GET_SESSION_SUCCESS,
  AUTH_GET_SESSION_ERROR,
  AUTH_SET_TRACKING_SESSION,
  AUTH_SET_TRACKING_SESSION_SUCCESS,
  AUTH_SET_TRACKING_SESSION_ERROR,
  AUTH_LOGIN_REQUEST,
  AUTH_FB_LOGIN_REQUEST,
  AUTH_LOGIN_SUCCESS,
  AUTH_LOGIN_ERROR,
  AUTH_REGISTER_REQUEST,
  AUTH_REGISTER_SUCCESS,
  AUTH_REGISTER_ERROR,
  AUTH_UPDATE_USER,
  AUTH_LOGOUT_REQUEST,
  AUTH_LOGOUT_SUCCESS,
  AUTH_RESET_STATE,
  AUTH_NOOP,
  setCustomSessionSuccess,
  indexCustomSessionSuccess,
  getSession,
  // getSessionSuccess,
  getSessionError,
  setTrackingSession,
  setTrackingSessionSuccess,
  setTrackingSessionError,
  login,
  fbLogin,
  loginSuccess,
  loginError,
  register,
  registerSuccess,
  registerError,
  updateUser,
  logout,
  logoutSuccess,
  resetAuthState,
  noop,
};

export const initialState: AuthState = { authenticated: false };

// Reducer
export default function reducer(state: AuthState = initialState,
                                action: AuthAction): AuthState {
  switch (action.type) {
    // We dont care about request or error because this is 'silent'
    /*case AUTH_GET_SESSION_ERROR:
      return {
        user: action.user,
        authenticated: true,
      };*/
    case AUTH_GET_SESSION_REQUEST:
      return update(state, {
        isAuthing: { $set: true },
      });
    case AUTH_GET_SESSION_ERROR:
      return update(state, {
        $unset: ['isAuthing'],
      });
    case AUTH_LOGIN_REQUEST:
    case AUTH_FB_LOGIN_REQUEST:
      return {
        isAuthing: true,
        authenticated: false,
      };
    case AUTH_LOGIN_SUCCESS:
      return {
        user: action.user,
        authenticated: true,
      };
    case AUTH_LOGIN_ERROR:
      return {
        authError: action.error,
        authenticated: false,
      };
    case AUTH_REGISTER_REQUEST:
      return {
        isRegistering: true,
        authenticated: false,
      };
    case AUTH_REGISTER_SUCCESS:
      return {
        user: action.user,
        authenticated: true,
        registerSuccessState: true,
      };
    case AUTH_REGISTER_ERROR:
      return {
        signUpError: action.error,
        authenticated: false,
      };
    case AUTH_LOGOUT_REQUEST:
    case AUTH_LOGOUT_SUCCESS:
      return {
        authenticated: false,
      };
    case AUTH_UPDATE_USER:
      return update(state, { user: { $merge: action.profileData } });
    case AUTH_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

// Epics
export function getSessionEpic(action$: Observable<AuthAction>,
                               state$: StateObservable<RootState>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_SET_TRACKING_SESSION_SUCCESS, AUTH_GET_SESSION_REQUEST),
    filter(() => !state$.value.auth.user && !state$.value.auth.authenticated),
    mergeMap(_ => getSessionApi().pipe(
      map((response: User) => {
        analytics.setUserContext(response);
        return loginSuccess(response);
      }),
      catchError((e: HTTPError) => of(getSessionError(e)))
    )),
  );
}

export function setTrackingSessionEpic(action$: Observable<AuthAction>,
                                       state$: StateObservable<RootState>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_SET_TRACKING_SESSION),
    mergeMap(({ trackingData }) => setTrackingSessionApi(trackingData).pipe(
      map((anonymousId: string) => {
        analytics.analytics.user().anonymousId(anonymousId);
        // Page view when lands on page only once, the first time
        // if (!window.firstTracking) {
        /*analytics.pageView('', {
          title: document.title,
          url: document.location.href,
          path: document.location.pathname,
          referrer: document.referrer,
          search: document.location.search,
        });*/
        window.firstTracking = true;
        if (window.pageViewQueue && window.pageViewQueue.length > 0) {
          window.pageViewQueue.forEach((fn: () => void) => fn());
          window.pageViewQueue = [];
        }
        //}
        if (!!state$.value.auth.user && !!state$.value.auth.authenticated) {
          analytics.setUserContext(state$.value.auth.user);
        }
        return setTrackingSessionSuccess(anonymousId);
      }),
      catchError((e: HTTPError) => of(setTrackingSessionError(e)))
    )),
  );
}

export function setCustomSessionEpic(action$: Observable<AuthAction>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_LOGIN_SUCCESS),
    mergeMap(_ => setCustomSessionApi().pipe(
      map(({ uuid }) => setCustomSessionSuccess(uuid)),
      catchError(e => of(noop())),
    )),
  );
}

export function executeAfterCustomCookieSetEpic(action$: Observable<AuthAction>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_SET_CUSTOM_SESSION_SUCCESS),
    mergeMap(({ uuid }) =>
      indexCustomSessionApi().pipe(
        map(_ => indexCustomSessionSuccess(uuid)),
        catchError(_ => of(noop())),
      )),
  );
}

export function executeAfterIndexCookieSetEpic(action$: Observable<AuthAction>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_INDEX_CUSTOM_SESSION_SUCCESS),
    mergeMap(({ uuid }) =>
      refreshSessionApi({ uuid }).pipe(
        map(_ => noop()),
        catchError(_ => of(noop())),
      )),
  );
}

export function changeLanguageAfterSuccessEpic(action$: Observable<AuthAction>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_LOGIN_SUCCESS, AUTH_REGISTER_SUCCESS),
    map(({ user }) => changeLocale((user.languageCode).toLowerCase())));
}

// Only redirect of has redirect to
export function redirectAfterSuccess(action$: Observable<AuthAction>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_LOGIN_SUCCESS, AUTH_REGISTER_SUCCESS),
    filter(action => action.redirectTo),
    mergeMap(({ redirectTo }) => of(push(redirectTo))));
}

export function loginEpic(action$: Observable<AuthAction>,
                          state$: StateObservable<RootState>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_LOGIN_REQUEST),
    mergeMap(({ email, password, redirectTo }) => {
      const req = {
        email,
        password,
      };
      return loginApi(req).pipe(
        map((response: User) => {
          analytics.setUserContext(response);
          return loginSuccess(response, redirectTo);
        }),
        catchError((e: HTTPError) => {
          const newError = { ...e, data: { ...e.data, email } };
          return of(loginError(newError));
        }),
      );
    }),
  );
}

export function loginFbEpic(action$: Observable<AuthAction>,
                            state$: StateObservable<RootState>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_FB_LOGIN_REQUEST),
    mergeMap(({ country, token, language, email, redirectTo }) => {
      const req: { [key: string]: any } = {
        language,
        email,
        authToken: token,
      };
      if (country) {
        req.country = country;
      }
      return loginFbApi(req).pipe(
        map((response: User) => {
          analytics.setUserContext(response);
          return loginSuccess(response, redirectTo);
        }),
        catchError((e: HTTPError) => {
          let newError = { ...e, data: { ...e.data, email } };
          if (!email && e.data && e.data.detail) {
            newError = { ...e, data: { ...e.data, email: e.data.detail.email } };
          }
          return of(loginError(newError));
        }));
    }));
}

export function registerEpic(action$: Observable<AuthAction>,
                             state$: StateObservable<RootState>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_REGISTER_REQUEST),
    mergeMap(({ country, firstName, lastName, email, password, language, redirectTo }) => {
      const req: { [key: string]: any } = {
        firstName,
        lastName,
        email,
        password,
        language,
      };
      if (country) {
        req.country = country;
      }
      return registerApi(req).pipe(
        map((response: User) => {
          return registerSuccess(response, redirectTo);
        }),
        catchError((e: HTTPError) => {
          const newError = { ...e, data: { ...e.data, email } };
          return of(registerError(newError));
        }));
    }));
}

export function logoutEpic(action$: Observable<AuthAction>,
                           state$: StateObservable<RootState>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_LOGOUT_REQUEST),
    mergeMap(() => merge(
      of(push('/')),
      logoutApi().pipe(
        mergeMap(() => {
          // unregister from Sentry here because we want to track if logout error happens
          analytics.unsetUserContext();
          // Load again userReferrer and landingUrl
          window.loadUrlInfo();
          return of(logoutSuccess(), setTrackingSession(analytics.buildTrackingData(state$.value.locale)));
        }),
        catchError((e: HTTPError) => of(logoutSuccess())))
      )
    ));
}

export function logoutCustomSessionEpic(action$: Observable<AuthAction>): Observable<AuthAction> {
  return action$.pipe(
    ofType(AUTH_LOGOUT_REQUEST),
    mergeMap(_ => logoutCustomSessionApi().pipe(
      map(_ => noop()),
      catchError(_ => of(noop())),
    ))
  );
}



// WEBPACK FOOTER //
// ./src/modules/auth/duck.js